import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@mlm/ui/components/toast';
import { CoachButton, Field, PasswordInput } from '../components/primitives';
import { apiErrorPayload, changePassword, isRateLimited, logout } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { AuthFrame } from './auth-frame';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const forced = useAuthStore((s) => s.user?.mustChangePassword ?? false);
  const setMustChangePassword = useAuthStore((s) => s.setMustChangePassword);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) return setError('Use at least 8 characters.');
    if (next !== confirm) return setError('The new passwords do not match.');
    setBusy(true);
    try {
      await changePassword({ currentPassword: current, newPassword: next });
      setMustChangePassword(false);
      if (!forced) toast.success('Password changed.');
      navigate('/home', { replace: true });
    } catch (err) {
      setError(
        isRateLimited(err)
          ? 'Too many attempts. Please wait an hour and try again.'
          : apiErrorPayload(err)?.error === 'invalid_current_password'
            ? 'Your current password is not right.'
            : 'Could not change your password. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      eyebrow="Account"
      title={forced ? 'Set a new password' : 'Change password'}
      subtitle={
        forced ? 'An administrator reset your password. Choose a new one to continue.' : undefined
      }
    >
      <form onSubmit={onSubmit} className="space-y-5">
        <Field label={forced ? 'Temporary password' : 'Current password'} htmlFor="current">
          <PasswordInput
            id="current"
            autoComplete="current-password"
            value={current}
            onChange={setCurrent}
            required
          />
        </Field>
        <Field label="New password" htmlFor="new">
          <PasswordInput
            id="new"
            autoComplete="new-password"
            value={next}
            onChange={setNext}
            required
          />
        </Field>
        <Field label="Confirm new password" htmlFor="confirm">
          <PasswordInput
            id="confirm"
            autoComplete="new-password"
            value={confirm}
            onChange={setConfirm}
            required
          />
        </Field>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <CoachButton kind="gold" type="submit" block disabled={busy}>
          {busy ? 'Saving' : 'Save password'}
        </CoachButton>
        <CoachButton
          kind="quiet"
          onClick={() => {
            if (forced) void logout().then(() => navigate('/welcome', { replace: true }));
            else navigate(-1);
          }}
        >
          {forced ? 'Log out' : 'Cancel'}
        </CoachButton>
      </form>
    </AuthFrame>
  );
}
